import {
  EMAIL_PREFERENCE_FIELDS,
  OPTIONAL_EMAIL_CATEGORIES,
  isEmailCategoryOn,
  type EmailPreferenceField,
  type EmailPreferences,
  type OptionalEmailCategory,
} from '@cip/core/users';

/** The `users/{uid}` fields one preferences-page action writes. */
export type EmailPreferenceUpdate = Partial<
  Record<EmailPreferenceField | 'okToEmail', boolean>
>;

/**
 * The fields to write when a person flips one category's switch.
 *
 * `okToEmail` stays `false` exactly when every optional category is off:
 * turning a category on while it is `false` also turns the master switch on
 * and writes the other categories off, so they stay off; turning off the
 * last category that was on turns the master switch off.
 *
 * @param params.preferences - The person's current preferences.
 * @param params.category - The switch they flipped.
 * @param params.isOn - The switch's new position.
 * @return The fields to merge into `users/{uid}`.
 * @throws Never.
 */
export function planCategorySwitch({
  preferences,
  category,
  isOn,
}: {
  preferences: EmailPreferences;
  category: OptionalEmailCategory;
  isOn: boolean;
}): EmailPreferenceUpdate {
  const field = EMAIL_PREFERENCE_FIELDS[category];
  if (isOn) {
    if (preferences.okToEmail !== false) {
      return {[field]: true};
    }
    const update: EmailPreferenceUpdate = {okToEmail: true};
    for (const other of OPTIONAL_EMAIL_CATEGORIES) {
      update[EMAIL_PREFERENCE_FIELDS[other]] = other === category;
    }
    return update;
  }
  const isAnotherOn = OPTIONAL_EMAIL_CATEGORIES.some(
    other =>
      other !== category && isEmailCategoryOn({preferences, category: other}),
  );
  return isAnotherOn ? {[field]: false} : {[field]: false, okToEmail: false};
}

/**
 * The fields to write for "Turn off all optional emails".
 *
 * @return The fields to merge into `users/{uid}`.
 * @throws Never.
 */
export function planTurnOffAll(): EmailPreferenceUpdate {
  return {okToEmail: false};
}
